import { useState } from 'react';
import type { CursorPage, EmergencyAdminArrangementRow, Partner } from '@dru/types';
import { Alert, Button, Field, Input, Select, TextSkeleton } from '@dru/ui';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';

/**
 * Which hospitals and labs treat this company's employees in an emergency
 * (owner, 4 Nov 2026: Dr Ultimate's staff decide). Only an arranged venue
 * can look up the company's employees by code and send it emergency bills.
 */
export function EmergencyArrangements({ tenantId }: { tenantId: string }) {
  const rows = useQuery(() => api.get<{ items: EmergencyAdminArrangementRow[] }>(`/admin/emergency/arrangements?tenantId=${tenantId}`), [tenantId]);
  const partners = useQuery(() => api.get<CursorPage<Partner>>('/partners?limit=100&status=ACTIVE'), []);
  const [partnerId, setPartnerId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const arranged = new Set((rows.data?.items ?? []).map((r) => r.partner?.id));

  const add = async () => {
    setBusy(true); setError(null);
    try {
      await api.post('/admin/emergency/arrangements', { tenantId, partnerId, ...(note.trim() ? { note: note.trim() } : {}) });
      setPartnerId(''); setNote(''); rows.reload();
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const end = async (id: string) => {
    setError(null);
    try { await api.del(`/admin/emergency/arrangements/${tenantId}/${id}`); rows.reload(); }
    catch (e) { setError(errorMessage(e)); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h4 text-neutral-900">Emergency providers</h2>
        <p className="text-small text-neutral-600">Where this company's employees go in an emergency and are billed through Dr Ultimate. Only partners switched on as emergency providers can be added.</p>
      </div>
      {error && <Alert>{error}</Alert>}
      {rows.error ? <Alert>{rows.error}</Alert> : rows.loading ? <TextSkeleton lines={3} /> : (rows.data?.items ?? []).length === 0 ? (
        <p className="text-small text-neutral-600">No venue arranged yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-200 rounded-card border border-neutral-200">
          {rows.data!.items.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0">
                <div className="font-medium text-neutral-900">{r.partner?.name}</div>
                <div className="text-caption text-neutral-600">{[r.partner?.code, r.partner?.city, r.note].filter(Boolean).join(' · ')}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => end(r.id)} className="max-md:min-h-10">End</Button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <Field label="Add an emergency provider">
          {(p) => (
            <Select {...p} value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
              <option value="">Choose an emergency provider…</option>
              {(partners.data?.items ?? []).filter((x) => x.emergencyProvider && !arranged.has(x.id)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </Select>
          )}
        </Field>
        <Field label="Note">{(p) => <Input {...p} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Nearest casualty to Pune plant" />}</Field>
        <Button disabled={!partnerId} loading={busy} onClick={add} className="max-md:w-full">Add</Button>
      </div>
    </div>
  );
}
